import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    OnInit,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {LocalConfigurationService} from "@fedelta-pos/data-client";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {Page} from "../../navigation/page";
import {LoadingStatus} from '../../session/loading-status';
import {Local} from "../../configuration/config";
import {ConfigurationModel, ParamConfig} from "../../configuration/configuration.model";
import {CardBrandComponent} from "../../ui-elements/card-brand/card-brand.component";
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderTransferTargetReferenceData} from '../../group-order/order-transfer-target-entity.model';
import {IdentityModel} from '../../identity/identity.model';
import {OrderType} from '../../order/order';
import {OrderDetailsPanelComponent} from '../../order/order-details-panel/order-details-panel.component';
import {OrderTotalsPanelComponent} from '../../order/order-totals-panel/order-totals-panel.component';
import {OrderInitiationStatus, OrderModel, OrderSubmissionStatus} from '../../order/order.model';
import {RecentOrderData} from '../../order/recent-order.service';
import {
    OrderingContextPanelComponent
} from '../../ordering-context/ordering-context-panel/ordering-context-panel.component';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {EmailFieldComponent} from '../../inputs/email-field/email-field.component';
import {FieldGroupComponent} from '../../inputs/form/field-group/field-group.component';
import {FieldPairComponent} from '../../inputs/form/field-pair/field-pair.component';
import {FormComponent} from '../../inputs/form/form.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {RadioFieldComponent} from '../../inputs/radio-field/radio-field.component';
import {SelectorControlComponent} from '../../inputs/selector-control/selector-control.component';
import {TextAreaFieldComponent} from '../../inputs/text-area-field/text-area-field.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {CustomerDetailsService} from '../../customer-info/customer-details.service';
import {IntegratedPaymentProvider, PaymentCard, PaymentType} from '../payment';
import {PaymentModel} from "../payment.model";
import {FullPageLayoutComponent} from '../../page-layout/full-page-layout/full-page-layout.component';
import {
    StripeManualPaymentCardPanelComponent
} from "../card-payment/stripe-manual-payment-card-panel/stripe-manual-payment-card-panel.component";
import {
    TyroManualPaymentCardPanelComponent
} from "../card-payment/tyro-manual-payment-card-panel/tyro-manual-payment-card-panel.component";
import {
    AdyenManualPaymentCardPanelComponent
} from "../card-payment/adyen-manual-payment-card-panel/adyen-manual-payment-card-panel.component";
import {TipSelectionPanelComponent} from "../tip-selection-panel/tip-selection-panel.component";
import {LegalPanelComponent} from '../../legal/legal-panel/legal-panel.component';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {FormsModule} from '@angular/forms';
import {ApplePayButtonComponent} from '../wallet-payment/apple-pay-button/apple-pay-button.component';
import {GooglePayButtonComponent} from '../wallet-payment/google-pay-button/google-pay-button.component';
import {PhoneNumberDirective} from '../../inputs/phone-number-field/phone-number.directive';
import {EmailDirective} from '../../inputs/email-field/email.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {LinkComponent} from '../../buttons/link/link.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {OrderingPhase, SessionModel} from '../../session/session.model';
import {PossessivePipe} from '../../utils/possessive.pipe';

@Component({
    selector: "woa-payment-page",
    templateUrl: "./payment-page.component.html",
    styleUrl: "./payment-page.component.scss",
    providers: [{provide: Page, useExisting: PaymentPageComponent}],
    imports: [
        AdyenManualPaymentCardPanelComponent,
        ApplePayButtonComponent,
        ButtonComponent,
        CardBrandComponent,
        ContentMessageComponent,
        CurrencyPipe,
        EmailDirective,
        EmailFieldComponent,
        FieldGroupComponent,
        FieldPairComponent,
        FormComponent,
        FormsModule,
        FullPageLayoutComponent,
        GooglePayButtonComponent,
        IconComponent,
        LegalPanelComponent,
        LinkComponent,
        OrderDetailsPanelComponent,
        OrderingContextPanelComponent,
        OrderTotalsPanelComponent,
        PageFooterControlDirective,
        PhoneNumberDirective,
        PhoneNumberFieldComponent,
        PossessivePipe,
        RadioFieldComponent,
        ResponsiveDirective,
        SelectorControlComponent,
        StripeManualPaymentCardPanelComponent,
        TextAreaFieldComponent,
        TextFieldComponent,
        TipSelectionPanelComponent,
        TyroManualPaymentCardPanelComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentPageComponent extends Page implements OnInit {
    private identityModel: IdentityModel = inject(IdentityModel);
    private loadingService: LoadingService = inject(LoadingService);
    private localConfigurationService: LocalConfigurationService = inject(LocalConfigurationService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    public orderModel: OrderModel = inject(OrderModel);
    public paymentModel: PaymentModel = inject(PaymentModel);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);

    MessageLevel = MessageLevel;
    OrderSubmissionStatus = OrderSubmissionStatus;
    PaymentType = PaymentType;
    LoadingStatus = LoadingStatus;
    IntegratedPaymentProvider = IntegratedPaymentProvider;
    OrderInitiationStatus = OrderInitiationStatus;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);
    private readonly deliveryDetailsER: Signal<ElementRef | undefined> = viewChild("deliveryDetails", {read: ElementRef});
    private readonly customerDetailsER: Signal<ElementRef | undefined> = viewChild("customerDetails", {read: ElementRef});
    private readonly paymentCardER: Signal<ElementRef | undefined> = viewChild(PaymentCard, {read: ElementRef});
    private paymentCard: Signal<PaymentCard | undefined> = viewChild(PaymentCard);

    public readonly loadingStatus: WritableSignal<LoadingStatus> = signal<LoadingStatus>(undefined);
    public readonly paymentType: WritableSignal<PaymentType> = signal<PaymentType>(undefined);
    public readonly hideDetails: WritableSignal<boolean> = signal<boolean>(true);
    public readonly recentOrder: WritableSignal<RecentOrderData> = signal<RecentOrderData>(undefined);
    public readonly transferTarget: WritableSignal<OrderTransferTargetReferenceData> = signal<OrderTransferTargetReferenceData>(undefined);
    public readonly nrPaymentOptions: WritableSignal<number> = signal<number>(undefined);
    public readonly allowConsumerPaymentTypes: WritableSignal<boolean> = signal<boolean>(undefined);
    private readonly hideFooterButtons: WritableSignal<boolean> = signal<boolean>(true);
    private readonly tipSelected: WritableSignal<boolean> = signal<boolean>(undefined);

    public readonly showFooter: Signal<boolean> = computed<boolean>(() =>
        this.loadingStatus() === LoadingStatus.LOADED && !this.hideFooterButtons());

    public readonly walletPayment: Signal<boolean> = computed<boolean>(() =>
        this.paymentType() === PaymentType.APPLE_PAY || this.paymentType() === PaymentType.GOOGLE_PAY);

    public readonly paymentProviderProvidesCustomerDetails: Signal<boolean> = computed<boolean>(() =>
        this.walletPayment() && this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO);

    public readonly showNameField: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.showName() && ((this.identityModel.isGuestUser() && !this.paymentProviderProvidesCustomerDetails()) || this.configurationModel.isWaiterActor()));

    public readonly showPhoneNumberField: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.showPhoneNumber() && !this.paymentProviderProvidesCustomerDetails() && this.paymentType() !== PaymentType.GROUP_PAY);

    public readonly showEmailField: Signal<boolean> = computed<boolean>(() =>
        (this.orderingContextModel.showEmail() || this.orderingContextModel.showMarketingOptIn()) &&
        (this.identityModel.isGuestUser() || !this.configurationModel.loginIdentityIsEmail()) &&
        !this.paymentProviderProvidesCustomerDetails() && this.paymentType() !== PaymentType.GROUP_PAY);

    public readonly showCustomerDetails: Signal<boolean> = computed<boolean>(() =>
        (this.paymentType() !== undefined || this.configurationModel.isWaiterActor()) && !this.paymentProviderProvidesCustomerDetails() &&
        (this.showNameField() || this.showEmailField() || this.orderingContextModel.showMarketingOptIn() ||
            (this.showPhoneNumberField() && !this.orderingContextModel.isConfirmedDeliveryOrdering())));

    public readonly emailLabel: Signal<string> = computed<string>(() =>
        this.configurationModel.canSendOrderReceivedEmail() ? "woa.label.payment.taxInvoiceEmailAddress" : "woa.label.common.email");

    public readonly requireFirstName: Signal<boolean> = computed<boolean>(() =>
        (this.orderingContextModel.requireFirstName() && !this.walletPayment()) || this.paymentType() === PaymentType.GROUP_PAY);

    public readonly requireLastName: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.requireLastName() && !this.walletPayment() && this.paymentType() !== PaymentType.GROUP_PAY);

    public readonly requireEmail: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.requireEmail() && !this.walletPayment());

    public readonly requirePhoneNumber: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.requirePhoneNumber() && !this.walletPayment());

    public readonly showSavedCreditCardPayment: Signal<boolean> = computed<boolean>(() =>
        this.paymentModel.hasSavedCreditCard() && this.configurationModel.isConsumerActor());

    public readonly showNewCreditCardPayment: Signal<boolean> = computed<boolean>(() => true);

    public readonly showPointsPayment: Signal<boolean> = computed<boolean>(() =>
        !this.identityModel.isGuestUser() &&
        this.configurationModel.app()?.supportsRedemption && this.orderModel.orderTotal() > 0 &&
        !!this.orderModel.initiatedOrder().pointsRedeemable);

    public readonly canPayWithPoints: Signal<boolean> = computed<boolean>(() =>
        this.orderModel.initiatedOrder().memberAvailableRedemptionPoints > 0);

    public readonly showTips: Signal<boolean> = computed<boolean>(() =>
        this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO &&
        this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().allowTips &&
        !this.tipSelected() && this.paymentModel.canTip(this.paymentType()));

    constructor() {
        super();

        if (!this.orderModel.paymentRequired()) {
            this.paymentType.set(PaymentType.NONE);
        }

        // for display purposes only
        const recentOrderData: RecentOrderData = this.orderModel.buildRecentOrderData();
        recentOrderData.orderPlacement.paymentAuthorisation.tipAmount = this.orderModel.tipAmount();
        this.recentOrder.set(recentOrderData);

        this.tipSelected.set(this.orderingContextModel.tipSelected());

        effect(() => {
            if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATED) {
                const recentOrderData: RecentOrderData = this.orderModel.buildRecentOrderData();
                recentOrderData.orderPlacement.paymentAuthorisation.tipAmount = this.orderModel.tipAmount();
                this.recentOrder.set(recentOrderData);
            }
        });

        effect(async () => {
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_INVALID) {
                return this.dismiss();
            }
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_CANCELLED) {
                return this.orderModel.completeOrder();
            }
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.INCOMPLETE) {
                this.hideFooterButtons.set(false);
            }
        });
    }

    ngOnInit(): void {
        void this.load();
    }

    // noinspection JSUnusedGlobalSymbols
    override onActive(): void {
        this.sessionModel.orderingPhase.set(OrderingPhase.PAYMENT);
    }

    public async load(): Promise<void> {
        const paramConfig: ParamConfig = await this.paymentModel.configurationModel.getParamConfig();
        this.allowConsumerPaymentTypes.set(paramConfig.allowConsumerPaymentTypes);

        // For waiters, we may proceed to the tap-to-pay screen immediately, in which case it looks better if we don't display anything
        // while the order initiation is taking place.
        this.hideDetails.set(this.configurationModel.isWaiterActor() && !this.allowConsumerPaymentTypes());

        try {
            if ((this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY && this.orderingContextModel.currentStore().integratedDelivery) ||
                this.orderingContextModel.currentStore().paymentProvider === IntegratedPaymentProvider.TYRO ||
                this.configurationModel.isWaiterActor()) {
                this.loadingStatus.set(LoadingStatus.LOADING);
                await this.loadingService.performWithSpinner(async () => {
                    this.orderModel.requestOrderInitiation("payment - load", false);
                    await this.orderModel.untilOrderInitiationComplete();
                    await this.paymentModel.untilWalletPaymentProcessorCreationResolved();
                }, 30000);
            }
            this.loadingStatus.set(LoadingStatus.LOADED);

            this.nrPaymentOptions.set(
                (this.paymentModel.canTapToPay() ? 1 : 0) +
                (this.configurationModel.isConsumerActor() || this.allowConsumerPaymentTypes() ?
                    (this.orderModel.initiatedOrder().availableTransferTargets.length +
                        (this.showSavedCreditCardPayment() ? 1 : 0) +
                        (this.showNewCreditCardPayment() ? 1 : 0) +
                        (this.showPointsPayment() ? 1 : 0) +
                        (this.paymentModel.canMakeGooglePayPayment() ? 1 : 0) +
                        (this.paymentModel.canMakeApplePayPayment() ? 1 : 0))
                    : 0));
            if (this.nrPaymentOptions() === 1) {
                this.paymentType.set(this.paymentModel.canTapToPay() ? PaymentType.TAP_TO_PAY
                    : this.orderModel.initiatedOrder().availableTransferTargets.length ? PaymentType.GROUP_PAY
                        : this.showSavedCreditCardPayment() ? PaymentType.SAVED_CREDIT_CARD
                            : this.showNewCreditCardPayment() ? PaymentType.NEW_CREDIT_CARD
                                : this.showPointsPayment() ? PaymentType.POINTS
                                    : this.paymentModel.canMakeGooglePayPayment() ? PaymentType.GOOGLE_PAY
                                        : this.paymentModel.canMakeApplePayPayment() ? PaymentType.APPLE_PAY
                                            : undefined);
                if (this.paymentType() === PaymentType.GROUP_PAY) {
                    this.transferTarget.set(this.orderModel.initiatedOrder().availableTransferTargets[0]);
                }
            }

            await this.sessionModel.checkLiveMode();

            // If there is no payment required, and no further details to enter or confirm, we can immediately place the order.
            // Pickups and deliveries allow an order time to be selected.
            // In practice, this means that free table orders for members or where customer details are not required are the only cases where this applies.
            if (!this.orderModel.paymentRequired() && this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE && (!this.identityModel.isGuestUser() || !this.showCustomerDetails())) {
                void this.orderModel.placeOrder(PaymentType.NONE, this.transferTarget());
                return;
            }

            // For waiters, if tap-to-pay is the only payment option, and no further details need to be captured, initiate tap-to-pay immediately.
            if (this.configurationModel.isWaiterActor()) {
                if (this.paymentType() === PaymentType.TAP_TO_PAY && !this.orderingContextModel.showName() && !this.orderingContextModel.showEmail() && !this.orderingContextModel.showPhoneNumber()) {
                    await this.orderModel.placeOrder(PaymentType.TAP_TO_PAY);
                }
                this.hideDetails.set(false);
            }

            this.hideFooterButtons.set(false);
        } catch (error) {
            this.loadingStatus.set(LoadingStatus.FAILED);
            console.log("PaymentPage - load failed:");
            console.error(error);
        }
    }

    public setPaymentType(paymentType: PaymentType): void {
        this.paymentType.set(paymentType);
        if (this.form()) {
            this.form().markAsUnsubmitted();
        }
        if (this.paymentCard()) {
            this.paymentCard()!.markAsUnsubmitted();
        }
        if (paymentType == PaymentType.NEW_CREDIT_CARD) {
            void this.copyDefaultCardNr();
        }
    }

    public get firstName(): string {
        return this.orderingContextModel.firstName();
    }

    public set firstName(firstName: string) {
        if (firstName !== this.orderingContextModel.firstName()) {
            this.orderingContextModel.firstNameTrusted.set(true);
        }
        this.orderingContextModel.firstName.set(firstName);
    }

    public get lastName(): string {
        return this.orderingContextModel.lastName();
    }

    public set lastName(lastName: string) {
        if (lastName !== this.orderingContextModel.lastName()) {
            this.orderingContextModel.lastNameTrusted.set(true);
        }
        this.orderingContextModel.lastName.set(lastName);
    }

    public get email(): string {
        return this.orderingContextModel.email();
    }

    public set email(email: string) {
        if (email !== this.orderingContextModel.email()) {
            this.orderingContextModel.emailTrusted.set(true);
        }
        this.orderingContextModel.email.set(email ? email.toLowerCase() : email);
    }

    public get mobilePhone(): string {
        return this.orderingContextModel.mobilePhone();
    }

    public set mobilePhone(mobilePhone: string) {
        if (mobilePhone !== this.orderingContextModel.mobilePhone()) {
            this.orderingContextModel.mobilePhoneTrusted.set(true);
        }
        this.orderingContextModel.mobilePhone.set(mobilePhone);
    }

    public tipChanged(): void {
        const recentOrderData: RecentOrderData = this.orderModel.buildRecentOrderData();
        recentOrderData.orderPlacement.paymentAuthorisation.tipAmount = this.orderModel.tipAmount();
        this.recentOrder.set(recentOrderData);
    }

    public selectTransferTarget(transferTarget: OrderTransferTargetReferenceData): void {
        this.setPaymentType(PaymentType.GROUP_PAY);
        this.transferTarget.set(transferTarget);
    }

    public forgetCard(): void {
        this.customerDetailsService.removePaymentCustomer();
    }

    public async copyDefaultCardNr(): Promise<void> {
        if (await this.localConfigurationService.getBoolean(Local.DEMO)) {
            try {
                const result: any = await navigator.permissions.query({name: "clipboard-write" as unknown as PermissionName});
                if (result.state === "granted" || result.state === "prompt") {
                    // noinspection JSUnresolvedReference
                    await navigator["clipboard"].writeText(this.paymentModel.paymentProvider() === IntegratedPaymentProvider.STRIPE ? "4000000360000006"
                        : this.paymentModel.paymentProvider() === IntegratedPaymentProvider.TYRO ? "4111111111111111"
                            : this.paymentModel.paymentProvider() === IntegratedPaymentProvider.OOLIO ? "4002 6900 0000 0008"
                                : "11111111111111111111");
                }
            } catch (error) {
            }
        }
    }

    public async placeOrder(): Promise<void> {

        // Card validation errors may be detectable prior to an attempted payment authorisation (e.g. stripe),
        // which is handled by the validate() callback passed to placeOrder().
        // Alternatively (e.g. tyro), card validation (and any other authorisation) errors may be detected only once
        // authorisation is attempted. A local validation error in this case is notified via the cardValidationFailed() callback.
        const paymentCard: PaymentCard | undefined = this.paymentCard();
        void this.orderModel.placeOrder(this.paymentType(), this.transferTarget(), paymentCard,
            () => {
                if (this.paymentType() === PaymentType.NEW_CREDIT_CARD && !paymentCard?.isValid()) {
                    setTimeout(() => this.paymentCardER()!.nativeElement.scrollIntoView({
                        block: "start",
                        inline: "nearest",
                        behavior: "smooth"
                    }), 330);
                    return false;
                }

                if (!this.form().valid) {
                    const fieldGroup: ElementRef | undefined = this.deliveryDetailsER() || this.customerDetailsER();
                    if (fieldGroup) {
                        setTimeout(() => fieldGroup.nativeElement.scrollIntoView({
                            block: "start",
                            inline: "nearest",
                            behavior: "smooth"
                        }), 330);
                    }
                    return false;
                }

                return true;
            },
            () => {
                if (this.paymentType() === PaymentType.NEW_CREDIT_CARD && this.paymentCardER()) {
                    setTimeout(() => this.paymentCardER()!.nativeElement.scrollIntoView({
                        block: "start",
                        inline: "nearest",
                        behavior: "smooth"
                    }), 330);
                }
            });
    }
}
